import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  STATES, SITE_TYPES, ENTITY_TYPES, WORKFORCE_MODELS,
  HIGH_INTENSITY_SUPPORTS, COHORTS, TIMELINE_DRIVERS,
} from "../../data/constants";
import { SectionHeader, Field, CheckRow, CertBadge, RadioRow } from "./formHelpers";

function Section({ children }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-6">{children}</CardContent>
    </Card>
  );
}

export function Section1ClientDetails({ form, upd }) {
  return (
    <Section>
      <SectionHeader id="s1" step="01" title="Client details" />
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Organisation / client name" required>
          <Input value={form.org_name} onChange={(e) => upd("org_name", e.target.value)} data-testid="input-org-name" />
        </Field>
        <Field label="ABN"><Input value={form.abn} onChange={(e) => upd("abn", e.target.value)} data-testid="input-abn" /></Field>
        <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} data-testid="input-contact-name" /></Field>
        <Field label="Contact phone"><Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} data-testid="input-contact-phone" /></Field>
        <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} data-testid="input-contact-email" /></Field>
        <Field label="Discovery date"><Input type="date" value={form.discovery_date} onChange={(e) => upd("discovery_date", e.target.value)} data-testid="input-discovery-date" /></Field>
        <Field label="Target registration date"><Input type="date" value={form.target_registration_date} onChange={(e) => upd("target_registration_date", e.target.value)} data-testid="input-target-date" /></Field>
      </div>
    </Section>
  );
}

export function Section2BusinessStructure({ form, upd }) {
  return (
    <Section>
      <SectionHeader id="s2" step="02" title="Business structure" />
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Entity type">
          <Select value={form.entity_type} onValueChange={(v) => upd("entity_type", v)}>
            <SelectTrigger data-testid="select-entity-type"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{ENTITY_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Workforce model">
          <Select value={form.workforce_model} onValueChange={(v) => upd("workforce_model", v)}>
            <SelectTrigger data-testid="select-workforce"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{WORKFORCE_MODELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Estimated worker count at audit">
          <Input type="number" min="0" value={form.worker_count}
            onChange={(e) => upd("worker_count", e.target.value)} data-testid="input-worker-count" />
        </Field>
        <Field label="AHPRA-registered or professionally regulated workers?">
          <RadioRow value={form.ahpra_regulated} onChange={(v) => upd("ahpra_regulated", v)} name="ahpra" />
        </Field>
      </div>
    </Section>
  );
}

export function Section3Geography({ form, toggle }) {
  return (
    <Section>
      <SectionHeader id="s3" step="03" title="Geographic reach & operating sites" />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">States / territories</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {STATES.map((s) => {
          const active = form.states.includes(s);
          return (
            <button
              type="button" key={s} onClick={() => toggle("states", s)}
              className={`text-sm font-medium px-3 py-1.5 rounded-md border transition-colors ${active ? "bg-[#E6F4F1] text-[#1A7F6E] border-[#1A7F6E]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
              data-testid={`state-${s}`}
            >{s}</button>
          );
        })}
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Operating site types</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {SITE_TYPES.map((s) => (
          <CheckRow key={s.key} id={`site-${s.key}`} checked={form.site_types.includes(s.key)}
            onCheckedChange={() => toggle("site_types", s.key)} label={s.label} />
        ))}
      </div>
    </Section>
  );
}

export function Section4RegistrationGroups({ form, toggle, groupedRegs }) {
  return (
    <Section>
      <SectionHeader id="s4" step="04" title="Registration groups"
        subtitle="Any CERT group selected forces the entire application to a Certification audit." />
      <div className="space-y-6">
        {Object.entries(groupedRegs).map(([cat, groups]) => (
          <div key={cat}>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{cat}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {groups.map((g) => (
                <CheckRow
                  key={g.key} id={`rg-${g.key}`}
                  checked={form.registration_groups.includes(g.key)}
                  onCheckedChange={() => toggle("registration_groups", g.key)}
                  label={`${g.code} ${g.name}`}
                  badge={<CertBadge cert={g.cert} modules={g.modules} />}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Section5RestrictivePractices({ form, upd }) {
  return (
    <Section>
      <SectionHeader id="s5" step="05" title="Restrictive practices" />
      <div className="space-y-4">
        <Field label="Will any supports involve regulated restrictive practices?">
          <RadioRow value={form.rp_regulated} onChange={(v) => upd("rp_regulated", v)} name="rp" />
        </Field>
        <Field label="Will you develop behaviour support plans?">
          <RadioRow value={form.bsp_develop} onChange={(v) => upd("bsp_develop", v)} name="bsp-dev" />
        </Field>
        <Field label="Will you implement behaviour support plans developed by others?">
          <RadioRow value={form.bsp_implement} onChange={(v) => upd("bsp_implement", v)} name="bsp-impl" />
        </Field>
      </div>
    </Section>
  );
}

export function Section6HighIntensity({ form, toggle }) {
  return (
    <Section>
      <SectionHeader id="s6" step="06" title="High intensity supports (Module 1)"
        subtitle="Any tick triggers Module 1 and HISSD worker competency evidence." />
      <div className="grid sm:grid-cols-2 gap-2">
        {HIGH_INTENSITY_SUPPORTS.map((s) => (
          <CheckRow key={s.key} id={`hi-${s.key}`} checked={form.high_intensity.includes(s.key)}
            onCheckedChange={() => toggle("high_intensity", s.key)} label={s.label} />
        ))}
      </div>
    </Section>
  );
}

export function Section7Cohorts({ form, toggle }) {
  return (
    <Section>
      <SectionHeader id="s7" step="07" title="Participant cohorts" />
      <div className="grid sm:grid-cols-2 gap-2">
        {COHORTS.map((c) => (
          <CheckRow key={c.key} id={`co-${c.key}`} checked={form.cohorts.includes(c.key)}
            onCheckedChange={() => toggle("cohorts", c.key)} label={c.label} meta={c.meta} />
        ))}
      </div>
    </Section>
  );
}

export function Section8Documents({ form, toggle, docsByGroup }) {
  return (
    <Section>
      <SectionHeader id="s8" step="08" title="Existing documents"
        subtitle="Tick everything currently held. Anything missing that's required will appear in the report." />
      <div className="space-y-6">
        {Object.entries(docsByGroup).map(([g, docs]) => (
          <div key={g}>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{g}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {docs.map((d) => (
                <CheckRow key={d.key} id={`doc-${d.key}`} checked={form.documents_held.includes(d.key)}
                  onCheckedChange={() => toggle("documents_held", d.key)} label={d.name} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Section9Timeline({ form, upd }) {
  return (
    <Section>
      <SectionHeader id="s9" step="09" title="Timeline & commercial" />
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Timeline driver">
          <Select value={form.timeline_driver} onValueChange={(v) => upd("timeline_driver", v)}>
            <SelectTrigger data-testid="select-timeline"><SelectValue placeholder="Select driver…" /></SelectTrigger>
            <SelectContent>{TIMELINE_DRIVERS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Prepared for 4–6 week engagement?">
          <RadioRow value={form.prepared_4_6_weeks} onChange={(v) => upd("prepared_4_6_weeks", v)} name="prep46" noUnsure />
        </Field>
        <Field label="Budgeted for AQA audit fees?">
          <RadioRow value={form.aqa_budgeted} onChange={(v) => upd("aqa_budgeted", v)}
            name="aqa" thirdLabel="Needs guidance" thirdValue="needs_guidance" />
        </Field>
      </div>
    </Section>
  );
}
